<script lang="ts" setup>
import {computed, ref} from "vue";

const companySiret = ref("");
const normalizedSiret = computed(() => companySiret.value.replace(/\D/g, "").slice(0, 14));
const hasValidSiretLength = computed(() => normalizedSiret.value.length === 14);

const synchronizationLinks = [
  {
    title: "Produits",
    description: "Comparer les produits entre EVA et AMMON.",
    icon: "mdi-database",
    accent: "#226fa5",
    tint: "#eaf4fb",
    href: "/sync/eva/ammon/product",
  },
  {
    title: "Actions de formation",
    description: "Comparer les actions de formation entre EVA et AMMON.",
    icon: "mdi-school",
    accent: "#7651a8",
    tint: "#f2ecfa",
    href: "/sync/eva/ammon/course",
  },
  {
    title: "Séances",
    description: "Comparer les séances entre EVA et AMMON.",
    icon: "mdi-calendar-clock",
    accent: "#ae661b",
    tint: "#fff3e3",
    href: "/sync/eva/ammon/session",
  },
  {
    title: "Lieux",
    description: "Comparer les lieux entre EVA et AMMON.",
    icon: "mdi-map-marker",
    accent: "#21816e",
    tint: "#e7f5f0",
    href: "/sync/eva/ammon/location",
  },
]
</script>

<template>
  <v-container class="dashboard py-6 py-sm-8">
    <v-row>
      <v-col
        v-for="link in synchronizationLinks"
        :key="link.href"
        cols="12"
        sm="6"
      >
        <v-card
          :to="link.href"
          class="h-100 synchronization-card"
          :style="{'--card-accent': link.accent, '--card-tint': link.tint}"
          elevation="0"
        >
          <v-card-text class="pa-5">
            <div class="d-flex align-center ga-3 mb-4">
              <span class="card-icon">
                <v-icon :icon="link.icon" size="26" />
              </span>
              <h2 class="text-subtitle-1 font-weight-medium">{{ link.title }}</h2>
            </div>
            <p class="text-body-2 text-medium-emphasis">
              {{ link.description }}
            </p>
            <div class="card-link d-flex align-center justify-space-between text-body-2 font-weight-medium mt-5">
              Ouvrir la synchronisation
              <v-icon class="ml-2" icon="mdi-arrow-right" size="18" />
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12">
        <v-card class="company-card" elevation="0">
          <v-card-title class="d-flex align-center text-subtitle-1 pt-5 px-5">
            <span class="card-icon mr-3">
              <v-icon icon="mdi-domain" size="26" />
            </span>
            Entreprise et collaborateurs
          </v-card-title>
          <v-card-text class="px-5 pb-5">
            <p class="text-body-2 text-medium-emphasis">
              Saisissez un SIRET pour ouvrir la synchronisation de l’entreprise ou de ses collaborateurs.
            </p>
            <v-text-field
              id="company-siret"
              v-model="companySiret"
              class="mt-4 siret-input"
              density="compact"
              counter="14"
              inputmode="numeric"
              label="SIRET de l’entreprise"
              maxlength="14"
              variant="outlined"
            />
            <div class="d-flex flex-wrap ga-2">
              <v-btn
                :disabled="!hasValidSiretLength"
                :to="`/synchro/eva/ammon/${normalizedSiret}`"
                color="green"
                variant="flat"
              >
                Synchroniser l’entreprise
              </v-btn>
              <v-btn
                :disabled="!hasValidSiretLength"
                :to="{
                  path: `/company/${normalizedSiret}/users`,
                  query: {source: 'eva', target: 'ammon'},
                }"
                variant="outlined"
              >
                Synchroniser les collaborateurs
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
.dashboard {
  max-width: 960px;
}

p {
  margin: 0;
}

.siret-input {
  max-width: 360px;
}

.synchronization-card,
.company-card {
  border: 1px solid #dce3e7;
  border-radius: 14px;
  box-shadow: 0 3px 12px rgb(25 45 60 / 4%) !important;
}

.synchronization-card {
  color: inherit !important;
  border-top: 3px solid var(--card-accent);
  background: linear-gradient(135deg, var(--card-tint), white 65%);
  transition: transform 180ms ease, box-shadow 180ms ease;
}

.synchronization-card:hover,
.synchronization-card:focus-visible {
  transform: translateY(-3px);
  box-shadow: 0 8px 22px rgb(25 45 60 / 10%) !important;
}

.synchronization-card:focus-visible {
  outline: 2px solid var(--card-accent);
  outline-offset: 3px;
}

.card-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 12px;
  background: var(--card-tint);
  color: var(--card-accent);
}

.card-link {
  color: var(--card-accent);
}

.company-card {
  --card-accent: #198754;
  --card-tint: #e7f5ed;
  border-top: 3px solid var(--card-accent);
}

@media (prefers-reduced-motion: reduce) {
  .synchronization-card {
    transition: none;
    transform: none;
  }
}
</style>
